import { useEffect, useCallback } from 'react';
import { SimulationConfig, Ball } from '../types';
import { STORAGE_KEY } from '../constants';

export const usePhysicsWorker = (
  config: SimulationConfig,
  ballsRef: React.MutableRefObject<Ball[]>,
  workerRef: React.MutableRefObject<Worker | null>,
  onCollision?: (payload: any) => void
) => {
  useEffect(() => {
    workerRef.current = new Worker(new URL('../physics.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current.onmessage = (e) => {
        if (e.data.type === 'UPDATE') {
            const { data, count } = e.data;
            const STRIDE = 12;
            for (let i = 0; i < count; i++) {
                if (ballsRef.current[i]) {
                    ballsRef.current[i].pos.x = data[i * STRIDE + 0];
                    ballsRef.current[i].pos.y = data[i * STRIDE + 1];
                    ballsRef.current[i].vel.x = data[i * STRIDE + 2];
                    ballsRef.current[i].vel.y = data[i * STRIDE + 3];
                    ballsRef.current[i].radius = data[i * STRIDE + 4];
                    ballsRef.current[i].mass = data[i * STRIDE + 5]; // Update mass too!
                    ballsRef.current[i].angle = data[i * STRIDE + 9];
                }
            }
        } else if (e.data.type === 'COLLISION') {
            onCollision?.(e.data.payload);
        }
    };
    
    return () => workerRef.current?.terminate();
  }, [onCollision]);

  useEffect(() => {
      if (workerRef.current) {
          workerRef.current.postMessage({ 
              type: 'INIT', 
              payload: { 
                  config: config, 
                  width: window.innerWidth, 
                  height: window.innerHeight,
                  maxBalls: config.maxBalls
              } 
          });
      }
  }, []);

  useEffect(() => {
      if (workerRef.current && ballsRef.current.length > 0) {
          // Only send balls if the worker doesn't have them yet or if they were reset
          // For simplicity, we can send them when the length changes significantly or on init
          workerRef.current.postMessage({ 
              type: 'ADD_BALLS', 
              payload: { balls: ballsRef.current } 
          });
      }
  }, [config.mode]); // Re-send when mode changes or on first load

  useEffect(() => {
    if (workerRef.current) {
        workerRef.current.postMessage({ type: 'UPDATE_CONFIG', payload: config });
    }
  }, [config]);

  const resetWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'CLEAR_BALLS' });
    }
  }, [workerRef]);

  const sendBallsToWorker = useCallback((balls: Ball[]) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls } });
    }
  }, [workerRef]);

  return { resetWorker, sendBallsToWorker };
};
